import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { initMapFactory, queryMapFactory, DefaultFailureAction, DefaultSuccessAction, initMultiMapFactory, dataHookWithCredentialsFactory, readonlyHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { dateRangeOverlap } from "../../common/utils/dateUtils";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { ensureInteger } from "../../common/utils/generalUtils";
import { shiftIdString } from "../shift/shiftMgrSlice";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = shift => ensureInteger(shift.id);

const prepareArray = data => ensureArray(data).map(shift => ({
    ...shift,
    renderid: shiftIdString(shift)
}))

export const shiftStaffSlice = createSlice({
    name: 'staffShifts',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            prepareArray(action.payload.data).forEach(shift => state.data[key(shift)] = shift);
        },
        addOrUpdate: (state, action) => {
            prepareArray(action.payload).forEach(s => state.data[key(s)] = s);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(s => delete state.data[key(s)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => shiftStaffSlice.getInitialState());
    }
});


// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'shift_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'shift_one');

const fetchShift = aggregatingFetch(
    id => network => network.post('/api/v2/staffShifts', [id], data => shiftStaffSlice.actions.set({ data, ids: [id] }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/staffShifts', ids, data => shiftStaffSlice.actions.set({ data, ids: ensureArray(ids) }), visualizeFailure('get')),
    { primitiveKey: true }
);

const fetchStaffShiftsInRange = (start, end) => network => network.get(`/api/v2/staffShifts/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, shiftStaffSlice.actions.addOrUpdate, visualizeFailure('get'));
// #endregion


// #region selectors
const selectShiftData = state => state.staffShifts.data;
const selectShift = id => createSelector([selectShiftData], shifts => shifts[id]);
const selectStaffShifts = inputIds => {
    const ids = ensureArray(inputIds);
    return createSelector([selectShiftData], shifts => ids.reduce((res, id) => {
        if (shifts[id]) {
            res.push(shifts[id]);
        }
        return res;
    }, []));
}
const selectStaffShiftsInRange = (start, end) => createSelector([selectShiftData], shifts => {
    let result = Object.values(shifts).filter(s => dateRangeOverlap(s, { start, end }));
    result.sort((a, b) => (new Date(a.start)).getTime() - (new Date(b.start)).getTime());
    return result;
});

const listen = {
    shiftStaffCreated: (shift, { dispatch, websocket }) => {
        markFetched(websocket, 'shift', key(shift))
        dispatch(shiftStaffSlice.actions.addOrUpdate(shift))
    },
    shiftStaffUpdated: (shift, { dispatch }) => dispatch(shiftStaffSlice.actions.addOrUpdate(shift)),
    shiftStaffRemoved: (shift, { dispatch }) => dispatch(shiftStaffSlice.actions.remove(shift))
}

// have to use the same factory for the single and multiple gets so that single and multiple deps are unified

const shiftInitByID = initMapFactory(fetchShift, listen, { name: 'staffShiftByID' });
registerEntityBasedFetch('shift', shiftInitByID);

const options = {
    cleanup: { root: shiftStaffSlice.name, all: state => Object.values(selectShiftData(state)), removeAll: shiftStaffSlice.actions.remove, key },
    condition: credentials => credentials?.hasRole('member')
}

const optionsSingle = { ...options, defaultValue: null}

export const [useStaffShifts] = dataHookWithCredentialsFactory(selectStaffShifts, initMultiMapFactory(shiftInitByID, listen), options);
export const [useStaffShift, realizeStaffShift] = dataHookWithCredentialsFactory(selectShift, shiftInitByID, { ...options, defaultValue: null });
export const [useStaffShiftsInRange] = dataHookWithCredentialsFactory(selectStaffShiftsInRange, queryMapFactory(fetchStaffShiftsInRange, 'id', shiftInitByID, listen), options);

export const [useReadStaffShift] = readonlyHookWithCredentialsFactory(selectShift, id => shiftInitByID, optionsSingle);
export const [useReadStaffShifts] = readonlyHookWithCredentialsFactory(selectStaffShifts, ids => shiftInitByID, options);


const reducer = { name: shiftStaffSlice.name, reducer: shiftStaffSlice.reducer }
export default reducer;